import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import {
  Settings,
  Zap,
  ArrowLeft,
  ArrowRight,
  Brain,
  Sparkles,
  Sliders,
  Target,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { JobData } from '../shared/types';
import {
  STEP_WRAPPER_CLASS,
  STEP_MOTION_CLASS,
  STEP_CARD_COMPACT_CLASS,
  STEP_SCROLL_AREA_CLASS,
  STEP_INNER_HEADER_CLASS,
  STEP_HEADER_ICON_CLASS,
  STEP_PRIMARY_CTA_CLASS,
  STEP_NAV_CLASS,
} from '../shared/stepLayout';
import { usePlanStatus } from '@soVendor/hooks/usePlanStatus';

const FOCUS_ICON_SELECTED_CLASS =
  'bg-indigo-600 text-white dark:bg-[#5B8DFF]';
const FOCUS_ICON_IDLE_CLASS = 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

interface OptimizationSettingsStepProps {
  onNext: (model: string, preferences: any) => void;
  onBack: () => void;
  jobData?: JobData;
  hasEnoughCredits: boolean;
  isLoading: boolean;
  error: string | null;
}

const OptimizationSettingsStep: React.FC<OptimizationSettingsStepProps> = ({
  onNext,
  onBack,
  jobData,
  hasEnoughCredits,
  isLoading
}) => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const [selectedModel, setSelectedModel] = useState<string>('groq-llama-3.3-70b-versatile');
  const [optimizationMode, setOptimizationMode] = useState<'balanced' | 'aggressive' | 'conservative'>('balanced');
  const [focusAreas, setFocusAreas] = useState<string[]>(['keywords', 'formatting', 'content']);
  const [writingTone, setWritingTone] = useState<'professional' | 'casual' | 'executive'>('professional');
  const [detailLevel, setDetailLevel] = useState<'concise' | 'detailed' | 'comprehensive'>('detailed');

  const [advancedOpen, setAdvancedOpen] = useState(false);

  const planStatus = usePlanStatus();
  const planKey = planStatus.planKey;

  // While the user still has credits, unlock ALL models so they can experience the full product.
  // Model restrictions only apply once free credits are exhausted.
  const isOutOfCredits = planStatus.credits !== null && planStatus.credits <= 0 && planKey === 'free';

  // Plan access order used as fallback when credits are gone
  const PLAN_ORDER = ['free', 'daypass', 'weekly', 'monthly', 'legacy'] as const;
  const planIndex = (key: string) => {
    const i = PLAN_ORDER.indexOf(key as any);
    return i === -1 ? 0 : i;
  };
  const canUseModel = (minPlan: string) =>
    !isOutOfCredits || planIndex(planKey) >= planIndex(minPlan);

  // Models with their minimum plan requirement
  const models = [
    {
      id: 'groq-llama-3.3-70b-versatile',
      name: 'Llama 3.3 70B',
      badge: 'Groq',
      description: 'Fast AI — recommended for all plans',
      recommended: true,
      credits: 1,
      minPlan: 'free',
      features: ['Groq speed', 'Great quality', '1 credit'],
    },
    {
      id: 'groq-llama-3.1-8b-instant',
      name: 'Llama 3.1 8B Instant',
      badge: 'Groq',
      description: 'Fastest results — best for quick iterations',
      recommended: false,
      credits: 1,
      minPlan: 'free',
      features: ['Instant results', 'Groq speed', '1 credit'],
    },
    {
      id: 'groq-mixtral-8x7b-32768',
      name: 'Mixtral 8x7B',
      badge: 'Groq',
      description: 'Expert quality with 32K context window',
      recommended: false,
      credits: 1,
      minPlan: 'free',
      features: ['Expert quality', '32K context', '1 credit'],
    },
    {
      id: 'openai-gpt-4o-mini',
      name: 'GPT-4o Mini',
      badge: 'OpenAI',
      description: 'Balanced — smarter optimization, Day Pass+',
      recommended: false,
      credits: 1,
      minPlan: 'daypass',
      features: ['Smart optimization', 'Good quality', '1 credit'],
    },
    {
      id: 'openai-gpt-4o',
      name: 'GPT-4o',
      badge: 'OpenAI',
      description: 'Best quality — deep analysis, Weekly+',
      recommended: false,
      credits: 1,
      minPlan: 'weekly',
      features: ['Best quality', 'Deep analysis', '1 credit'],
    },
  ];

  const PLAN_LABELS: Record<string, string> = {
    free: 'Free',
    daypass: 'Day Pass',
    weekly: 'Weekly',
    monthly: 'Monthly',
    legacy: 'Legacy',
  };

  const modes = [
    {
      id: 'conservative',
      name: 'Conservative',
      description: 'Minimal changes, preserve original style',
      icon: ''
    },
    {
      id: 'balanced',
      name: 'Balanced',
      description: 'Optimal mix of preservation and enhancement',
      icon: ''
    },
    {
      id: 'aggressive',
      name: 'Aggressive',
      description: 'Maximum optimization for best ATS scores',
      icon: ''
    }
  ];

  const focusOptions = [
    { id: 'keywords', labelKey: 'focusKeywords', icon: Target },
    { id: 'formatting', labelKey: 'focusFormatting', icon: Settings },
    { id: 'content', labelKey: 'focusContent', icon: Sparkles },
    { id: 'structure', labelKey: 'focusStructure', icon: Sliders }
  ];

  const toggleFocusArea = (area: string) => {
    setFocusAreas(prev =>
      prev.includes(area)
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const handleStart = () => {
    const preferences = {
      mode: optimizationMode,
      focusAreas,
      writingTone,
      detailLevel,
      preserveOriginalStyle: optimizationMode === 'conservative',
      enhanceKeywords: focusAreas.includes('keywords'),
      improveFormatting: focusAreas.includes('formatting'),
      enhanceContent: focusAreas.includes('content'),
      improveStructure: focusAreas.includes('structure')
    };
    onNext(selectedModel, preferences);
  };

  // Auto-reset selected model if it's locked on current plan
  useEffect(() => {
    const current = models.find(m => m.id === selectedModel);
    if (current && !canUseModel(current.minPlan)) {
      setSelectedModel('groq-llama-3.3-70b-versatile');
    }
  }, [planKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedModelData = models.find(m => m.id === selectedModel);
  // Every optimization costs 1 credit — check credits regardless of model
  const canStart = !isLoading && hasEnoughCredits && focusAreas.length > 0 && canUseModel(models.find(m => m.id === selectedModel)?.minPlan ?? 'free');

  const whatToOptimizeRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!whatToOptimizeRef.current || !scrollAreaRef.current) return;
    const timer = setTimeout(() => {
      whatToOptimizeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className={isNative ? '' : STEP_MOTION_CLASS}
      style={isNative ? { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', isolation: 'isolate' as const } : undefined}
    >
      {/* Native splash header */}
      {isNative && (
        <div
          className="flex-shrink-0 flex items-center justify-between px-4 pb-3"
          style={{
            background: 'var(--ggj-header-bg)',
            paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
            borderRadius: '0 0 24px 24px',
            boxShadow: 'var(--ggj-btn-shadow, 0 4px 16px rgba(0,0,0,0.12))',
          }}
        >
          <button type="button" onClick={onBack} className="w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation" style={{ background: 'var(--ggj-header-btn-bg)', color: 'var(--ggj-header-btn-icon)' }}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-purple-200 uppercase tracking-widest">Resume Optimizer</span>
            <span className="text-base font-bold text-white leading-tight">Optimization Settings</span>
          </div>
          <div className="w-9 h-9" />
        </div>
      )}

      <div className={isNative ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : STEP_WRAPPER_CLASS}>
        {/* On mobile: scrollable area + sticky bottom CTA */}
        <div className={isNative ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : 'flex flex-col h-full min-h-0'}>
          <div ref={scrollAreaRef} className={isNative ? 'flex-1 overflow-y-auto scrollbar-hide' : `${STEP_SCROLL_AREA_CLASS} flex-1 pb-24 md:pb-0`}
            style={isNative ? { background: 'var(--ggj-bg-page)', WebkitOverflowScrolling: 'touch' } as React.CSSProperties : undefined}>
            <div className={isNative ? '' : STEP_CARD_COMPACT_CLASS}>
              {/* Header – hidden on native (replaced by splash header), sticky on web */}
              <div className={`${STEP_INNER_HEADER_CLASS} min-w-0 ${isNative ? 'hidden' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className={`${STEP_HEADER_ICON_CLASS} h-10 w-10`}>
                    <Brain className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold vscode-text-primary mb-0.5">{`[${t('optimizer.settingsStep.title')}]`}</h2>
                    <p className="text-sm vscode-text-secondary line-clamp-2 sm:line-clamp-none">{t('optimizer.settingsStep.subtitle')}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 min-w-0">
                {/* ── Native mobile redesign ── */}
                {isNative && (
                  <div className="md:hidden px-4 pt-5 pb-4 space-y-5">

                    {/* Optimization Mode */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'var(--ggj-accent-gradient)' }}>
                          <Sliders className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>Optimization Mode</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {modes.map((mode) => {
                          const isSelected = optimizationMode === mode.id;
                          return (
                            <button
                              key={mode.id}
                              type="button"
                              onClick={() => setOptimizationMode(mode.id as any)}
                              className="py-3 px-2 rounded-2xl border text-center text-xs font-semibold transition-all touch-manipulation"
                              style={isSelected ? {
                                background: 'var(--ggj-accent-gradient)',
                                borderColor: 'transparent',
                                color: '#fff',
                                boxShadow: 'var(--ggj-btn-shadow)',
                              } : {
                                background: 'var(--ggj-bg-card)',
                                borderColor: 'var(--ggj-border)',
                                color: 'var(--ggj-text-secondary)',
                              }}
                            >
                              {mode.name}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs mt-2 px-1" style={{ color: 'var(--ggj-text-secondary)' }}>
                        {modes.find(m => m.id === optimizationMode)?.description}
                      </p>
                    </div>

                    {/* What to Optimize */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'var(--ggj-accent-gradient)' }}>
                          <Target className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>What to Optimize</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        {focusOptions.map((option) => {
                          const Icon = option.icon;
                          const isSelected = focusAreas.includes(option.id);
                          return (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => toggleFocusArea(option.id)}
                              className="p-3.5 rounded-2xl border text-left flex items-center gap-2.5 transition-all touch-manipulation"
                              style={isSelected ? {
                                background: 'var(--brand-accent-bg)',
                                borderColor: 'var(--brand-accent-border)',
                              } : {
                                background: 'var(--ggj-bg-card)',
                                borderColor: 'var(--ggj-border)',
                              }}
                            >
                              <div
                                className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                                style={isSelected
                                  ? { background: 'var(--ggj-accent-gradient)' }
                                  : { background: 'var(--ggj-bg-page)', border: '1px solid var(--ggj-border)' }}
                              >
                                <Icon className="w-4 h-4" style={{ color: isSelected ? '#fff' : 'var(--ggj-text-secondary)' }} />
                              </div>
                              <span className="font-semibold text-xs flex-1 leading-tight"
                                style={{ color: isSelected ? '#6C47FF' : 'var(--ggj-text-primary)' }}>
                                {t(`optimizer.settingsStep.${option.labelKey}`)}
                              </span>
                              {isSelected && (
                                <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
                                  style={{ background: 'var(--ggj-accent-gradient)' }}>
                                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                    <path d="M1.5 4L3.5 6L6.5 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* AI Model selector */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'var(--ggj-accent-gradient)' }}>
                          <Brain className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>AI Model</span>
                      </div>
                      <div
                        className="rounded-2xl border overflow-hidden"
                        style={{ background: 'var(--ggj-bg-card)', borderColor: 'var(--ggj-border)' }}
                      >
                        <select
                          value={selectedModel}
                          onChange={(e) => canUseModel(models.find(m => m.id === e.target.value)?.minPlan ?? 'free') && setSelectedModel(e.target.value)}
                          className="w-full px-4 py-3.5 text-sm font-medium bg-transparent border-0 outline-none touch-manipulation cursor-pointer"
                          style={{ color: 'var(--ggj-text-primary)' }}
                        >
                          {models.map((model) => {
                            const unlocked = canUseModel(model.minPlan);
                            const lockSuffix = !unlocked ? ` (${PLAN_LABELS[model.minPlan]}+)` : '';
                            const recommendedSuffix = model.recommended ? ' - Recommended' : '';
                            return (
                              <option key={model.id} value={model.id} disabled={!unlocked}>
                                {model.name} [{model.badge}]{recommendedSuffix}{lockSuffix}
                              </option>
                            );
                          })}
                        </select>
                        {selectedModelData && (
                          <div className="px-4 pb-3 text-xs" style={{ color: 'var(--ggj-text-secondary)', borderTop: '1px solid var(--ggj-border)' }}>
                            <span className="pt-2 block">{selectedModelData.description}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Advanced – collapsible */}
                    <div>
                      <button
                        type="button"
                        onClick={() => setAdvancedOpen(o => !o)}
                        className="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border touch-manipulation"
                        style={{ background: 'var(--ggj-bg-card)', borderColor: 'var(--ggj-border)', color: 'var(--ggj-text-secondary)' }}
                      >
                        <span className="flex items-center gap-2 text-sm font-semibold">
                          <Settings className="w-4 h-4" />
                          [Advanced Settings]
                        </span>
                        {advancedOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>

                      {advancedOpen && (
                        <div className="mt-2 space-y-3 px-1">
                          <div>
                            <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--ggj-text-secondary)' }}>Writing Tone</label>
                            <select value={writingTone} onChange={(e) => setWritingTone(e.target.value as any)}
                              className="w-full px-4 py-3 rounded-2xl border text-sm font-medium bg-transparent outline-none touch-manipulation cursor-pointer"
                              style={{ background: 'var(--ggj-bg-card)', borderColor: 'var(--ggj-border)', color: 'var(--ggj-text-primary)' }}>
                              <option value="professional">Professional</option>
                              <option value="casual">Casual</option>
                              <option value="executive">Executive</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--ggj-text-secondary)' }}>Detail Level</label>
                            <select value={detailLevel} onChange={(e) => setDetailLevel(e.target.value as any)}
                              className="w-full px-4 py-3 rounded-2xl border text-sm font-medium bg-transparent outline-none touch-manipulation cursor-pointer"
                              style={{ background: 'var(--ggj-bg-card)', borderColor: 'var(--ggj-border)', color: 'var(--ggj-text-primary)' }}>
                              <option value="concise">Concise</option>
                              <option value="detailed">Detailed</option>
                              <option value="comprehensive">Comprehensive</option>
                            </select>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Credit info */}
                    <div className="rounded-2xl border px-4 py-3.5 flex items-center justify-between"
                      style={{ background: 'var(--brand-accent-bg)', borderColor: 'var(--brand-accent-border)' }}>
                      <div>
                        <p className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>Total Cost</p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--ggj-text-secondary)' }}>1 credit per optimization</p>
                      </div>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl"
                        style={{ background: 'var(--ggj-accent-gradient)', boxShadow: 'var(--ggj-btn-shadow)' }}>
                        <Zap className="w-3.5 h-3.5 text-white" />
                        <span className="text-sm font-bold text-white">1 Credit</span>
                      </div>
                    </div>

                    {!hasEnoughCredits && (
                      <div className="rounded-2xl border px-4 py-3.5 flex items-start gap-3"
                        style={{ background: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.25)' }}>
                        <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-red-600">No credits remaining</p>
                          <p className="text-xs text-red-500 mt-0.5">Upgrade your plan to continue.</p>
                        </div>
                      </div>
                    )}

                    {/* Bottom spacer so content clears the CTA */}
                    <div className="h-2" />
                  </div>
                )}

                {/* ── Non-native mobile fallback ── */}
                {!isNative && (
                <div className="space-y-4 border-b border-solid p-4 md:hidden vscode-border">
                  {/* Optimization Mode */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">{t('optimizer.settingsStep.optimizationMode')}</label>
                    <div className="grid grid-cols-3 gap-2">
                      {modes.map((mode) => {
                        const isSelected = optimizationMode === mode.id;
                        return (
                          <button
                            key={mode.id}
                            type="button"
                            onClick={() => setOptimizationMode(mode.id as any)}
                            className={`p-3 min-h-[44px] rounded-xl border text-center text-sm font-medium transition-all touch-manipulation ${
                              isSelected
                                ? 'border-[#6C47FF] dark:border-[#5B8DFF] bg-[#6C47FF]/10 dark:bg-[#5B3FFF]/20 text-[#6C47FF] dark:text-[#5B8DFF]'
                                : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {t(`optimizer.settingsStep.mode${mode.id.charAt(0).toUpperCase() + mode.id.slice(1)}`)}
                          </button>
                        );
                      })}
                    </div>
                    <p className="vscode-text-secondary mt-1.5 text-xs">
                      {modes.find(m => m.id === optimizationMode) && t(`optimizer.settingsStep.mode${(optimizationMode as string).charAt(0).toUpperCase() + (optimizationMode as string).slice(1)}Desc`)}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4 text-[#6C47FF] dark:text-[#5B8DFF]" />
                      {t('optimizer.settingsStep.whatToOptimize')}
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {focusOptions.map((option) => {
                        const Icon = option.icon;
                        const isSelected = focusAreas.includes(option.id);
                        return (
                          <button key={option.id} type="button" onClick={() => toggleFocusArea(option.id)}
                            className={`p-3 min-h-[52px] rounded-xl border text-left flex items-center gap-2 transition-all touch-manipulation ${isSelected ? 'border-[#6C47FF] dark:border-[#5B8DFF] bg-[#6C47FF]/10 dark:bg-[#5B3FFF]/20' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50'}`}>
                            <div
                              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                                isSelected ? FOCUS_ICON_SELECTED_CLASS : FOCUS_ICON_IDLE_CLASS
                              }`}
                            >
                              <Icon className="h-3.5 w-3.5" />
                            </div>
                            <span className={`font-medium text-xs flex-1 leading-tight ${isSelected ? 'text-[#6C47FF] dark:text-[#5B8DFF]' : 'text-gray-700 dark:text-gray-300'}`}>{t(`optimizer.settingsStep.${option.labelKey}`)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('optimizer.settingsStep.totalCost')}</span>
                      <span className="font-semibold text-[#6C47FF] dark:text-[#5B8DFF]">1 {t('optimizer.settingsStep.credits')}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">1 credit per optimization, any AI model.</p>
                    {!hasEnoughCredits && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700 dark:text-red-300">No credits remaining. Upgrade to continue.</p>
                      </div>
                    )}
                  </div>
                  <button type="button" onClick={() => setAdvancedOpen(o => !o)}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300 touch-manipulation">
                    <span className="flex items-center gap-2"><Settings className="w-4 h-4" /> [Advanced Settings]</span>
                    {advancedOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {advancedOpen && (
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="model-select-mobile" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">{t('optimizer.settingsStep.selectAiModel')}</label>
                        <select id="model-select-mobile" value={selectedModel} onChange={(e) => canUseModel(models.find(m => m.id === e.target.value)?.minPlan ?? 'free') && setSelectedModel(e.target.value)}
                          className="w-full px-3 py-3 min-h-[44px] bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6C47FF] cursor-pointer touch-manipulation">
                          {models.map((model) => { const unlocked = canUseModel(model.minPlan); return (<option key={model.id} value={model.id} disabled={!unlocked}>{model.name} ({model.badge}){model.recommended ? ' ★' : ''}{!unlocked ? ` (${PLAN_LABELS[model.minPlan]}+)` : ''}</option>); })}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="writing-tone-mobile" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">{t('optimizer.settingsStep.writingTone')}</label>
                        <select id="writing-tone-mobile" value={writingTone} onChange={(e) => setWritingTone(e.target.value as any)} className="w-full px-3 py-3 min-h-[44px] bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6C47FF] cursor-pointer touch-manipulation">
                          <option value="professional">Professional</option><option value="casual">Casual</option><option value="executive">Executive</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="detail-level-mobile" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1.5">{t('optimizer.settingsStep.detailLevel')}</label>
                        <select id="detail-level-mobile" value={detailLevel} onChange={(e) => setDetailLevel(e.target.value as any)} className="w-full px-3 py-3 min-h-[44px] bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6C47FF] cursor-pointer touch-manipulation">
                          <option value="concise">Concise</option><option value="detailed">Detailed</option><option value="comprehensive">Comprehensive</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* ── Desktop: Left column (model & preferences) ── */}
                <div className="hidden min-w-0 space-y-4 border-b border-solid p-4 sm:p-5 md:block lg:border-b-0 lg:border-r lg:p-6 vscode-border">
                  <div>
                    <label htmlFor="model-select" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">{t('optimizer.settingsStep.selectAiModel')}</label>
                    <select
                      id="model-select"
                      value={selectedModel}
                      onChange={(e) => canUseModel(models.find(m => m.id === e.target.value)?.minPlan ?? 'free') && setSelectedModel(e.target.value)}
                      className="w-full px-4 py-3 min-h-[44px] bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6C47FF] focus:border-transparent cursor-pointer touch-manipulation"
                    >
                      {models.map((model) => {
                        const unlocked = canUseModel(model.minPlan);
                        const lockSuffix = !unlocked ? ` 🔒 ${isOutOfCredits ? '— Upgrade' : `— ${PLAN_LABELS[model.minPlan]}+`}` : '';
                        const recommendedSuffix = model.recommended ? ' ★' : '';
                        return (
                          <option key={model.id} value={model.id} disabled={!unlocked}>
                            {model.name} ({model.badge}){recommendedSuffix}{lockSuffix}
                          </option>
                        );
                      })}
                    </select>
                    {selectedModelData && <p className="vscode-text-secondary mt-1.5 text-sm">{selectedModelData.description}</p>}
                    {isOutOfCredits && (
                      <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('navigate-to-pricing'))} className="mt-2 flex items-center gap-1 text-xs text-[#6C47FF] hover:underline">
                        <Zap className="w-3 h-3" /> Upgrade to unlock GPT-4o Mini &amp; GPT-4o
                      </button>
                    )}
                  </div>
                  <div>
                    <label htmlFor="writing-tone" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">{t('optimizer.settingsStep.writingTone')}</label>
                    <select id="writing-tone" value={writingTone} onChange={(e) => setWritingTone(e.target.value as any)} className="w-full px-3 py-3 min-h-[44px] bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6C47FF] cursor-pointer touch-manipulation">
                      <option value="professional">{t('optimizer.settingsStep.toneProfessional')}</option>
                      <option value="casual">{t('optimizer.settingsStep.toneCasual')}</option>
                      <option value="executive">{t('optimizer.settingsStep.toneExecutive')}</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="detail-level" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">{t('optimizer.settingsStep.detailLevel')}</label>
                    <select id="detail-level" value={detailLevel} onChange={(e) => setDetailLevel(e.target.value as any)} className="w-full px-3 py-3 min-h-[44px] bg-gray-50 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#6C47FF] cursor-pointer touch-manipulation">
                      <option value="concise">{t('optimizer.settingsStep.detailConcise')}</option>
                      <option value="detailed">{t('optimizer.settingsStep.detailDetailed')}</option>
                      <option value="comprehensive">{t('optimizer.settingsStep.detailComprehensive')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">{t('optimizer.settingsStep.optimizationMode')}</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {modes.map((mode) => {
                        const isSelected = optimizationMode === mode.id;
                        return (
                          <button key={mode.id} type="button" onClick={() => setOptimizationMode(mode.id as any)}
                            className={`p-3 min-h-[44px] rounded-xl border text-center text-sm font-medium transition-all touch-manipulation ${isSelected ? 'border-[#6C47FF] dark:border-[#5B8DFF] bg-[#6C47FF]/10 dark:bg-[#5B3FFF]/20 text-[#6C47FF] dark:text-[#5B8DFF]' : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                          >
                            {t(`optimizer.settingsStep.mode${mode.id.charAt(0).toUpperCase() + mode.id.slice(1)}`)}
                          </button>
                        );
                      })}
                    </div>
                    <p className="vscode-text-secondary mt-1.5 text-xs">
                      {modes.find(m => m.id === optimizationMode) && t(`optimizer.settingsStep.mode${(optimizationMode as string).charAt(0).toUpperCase() + (optimizationMode as string).slice(1)}Desc`)}
                    </p>
                  </div>
                </div>

                {/* ── Desktop: Right column (focus areas & cost) ── */}
                <div ref={whatToOptimizeRef} className="hidden min-w-0 scroll-mt-4 flex-col p-4 pb-6 sm:p-6 sm:pb-8 md:flex lg:p-8">
                  <h3 className="vscode-text-primary mb-3 flex items-center gap-2 text-base font-semibold">
                    <Target className="w-4 h-4 text-[#6C47FF] dark:text-[#5B8DFF]" />
                    {t('optimizer.settingsStep.whatToOptimize')}
                  </h3>
                  <div className="space-y-2 mb-6">
                    {focusOptions.map((option) => {
                      const Icon = option.icon;
                      const isSelected = focusAreas.includes(option.id);
                      return (
                        <button key={option.id} type="button" onClick={() => toggleFocusArea(option.id)}
                          className="w-full p-3 min-h-[44px] rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 hover:border-gray-300 dark:hover:border-gray-600 text-left flex items-center gap-3 transition-all touch-manipulation"
                        >
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                              isSelected ? FOCUS_ICON_SELECTED_CLASS : FOCUS_ICON_IDLE_CLASS
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <span className={`font-medium text-sm flex-1 ${isSelected ? 'text-[#6C47FF] dark:text-[#5B8DFF]' : 'text-gray-700 dark:text-gray-300'}`}>{t(`optimizer.settingsStep.${option.labelKey}`)}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('optimizer.settingsStep.totalCost')}</span>
                      <span className="font-semibold text-amber-600 dark:text-amber-400">1 {t('optimizer.settingsStep.credits')}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">1 credit is deducted per optimization, regardless of AI model.</p>
                    {!hasEnoughCredits && (
                      <div className="mt-2 flex items-start gap-2 rounded-lg p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                        <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700 dark:text-red-300">You have no credits remaining. Upgrade your plan to continue.</p>
                      </div>
                    )}
                    <div className="mt-4 flex justify-end">
                      <button type="button" onClick={handleStart} disabled={!canStart} aria-label={t('optimizer.settingsStep.startOptimization')}
                        className={`flex touch-manipulation items-center justify-center gap-2 ${STEP_PRIMARY_CTA_CLASS} px-5 py-2.5 text-sm`}
                      >
                        <Zap className="w-4 h-4 shrink-0" />
                        <span>{`[${t('optimizer.settingsStep.startOptimization')}]`}</span>
                        <ArrowRight className="w-4 h-4 shrink-0" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {!isNative && (
            <nav className={`${STEP_NAV_CLASS} hidden md:flex`} aria-label="Step navigation">
              <button
                type="button"
                onClick={onBack}
                className="flex min-h-[44px] min-w-0 flex-initial touch-manipulation items-center justify-center gap-2 rounded-lg px-3 py-3 font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-white sm:px-4"
              >
                <ArrowLeft className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">{t('optimizer.baselineStep.backShort')}</span>
              </button>
            </nav>
          )}

          {/* ── Mobile sticky bottom CTA ── */}
          <div
            className={
              isNative
                ? 'flex-shrink-0 border-t px-4 pt-3'
                : 'safe-area-inset-bottom vscode-bg-secondary fixed bottom-0 left-0 right-0 z-50 flex gap-2 border-t border-solid px-4 py-3 backdrop-blur-md md:hidden vscode-border'
            }
            style={
              isNative
                ? {
                    background: 'var(--ggj-nav-bg)',
                    borderColor: 'var(--ggj-border)',
                    paddingBottom: 12,
                  }
                : undefined
            }
          >
            {!isNative && (
              <button
                type="button"
                onClick={onBack}
                className="flex min-h-[44px] shrink-0 touch-manipulation items-center justify-center gap-1.5 rounded-xl border border-gray-300 bg-gray-200/90 px-3 font-medium text-gray-800 transition-colors hover:bg-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                aria-label={t('optimizer.baselineStep.backShort')}
              >
                <ArrowLeft className="h-5 w-5 shrink-0" />
                <span className="max-w-[4.5rem] truncate text-sm sm:max-w-none">{t('optimizer.baselineStep.backShort')}</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleStart}
              disabled={!canStart}
              aria-label={t('optimizer.settingsStep.startOptimization')}
              className={
                isNative
                  ? 'w-full flex items-center justify-center gap-2 px-5 py-4 rounded-2xl text-base font-bold text-white transition-opacity disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation'
                  : `flex min-h-[44px] flex-1 touch-manipulation items-center justify-center gap-2 ${STEP_PRIMARY_CTA_CLASS} px-4 py-3 text-base font-bold`
              }
              style={
                isNative
                  ? {
                      background: canStart ? 'var(--ggj-accent-gradient)' : 'var(--ggj-border)',
                      boxShadow: canStart ? 'var(--ggj-btn-shadow)' : 'none',
                    }
                  : undefined
              }
            >
              <Zap className="h-5 w-5 shrink-0" />
              <span>[Start Optimization]</span>
              <ArrowRight className="h-5 w-5 shrink-0" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default React.memo(OptimizationSettingsStep);

